import { useApolloClient, useMutation, DataProxy } from '@apollo/client';
import { useCallback, useMemo, useContext, useEffect } from 'react';
import { debounce } from 'debounce';
import { FavoritesContext } from './FavoritesProvider';
import {
  ADD_FAVORITE_MUTATION,
  GET_FAVORITES_QUERY,
  REMOVE_FAVORITE_MUTATION,
  GetFavoritesResponse
} from './favorites.rest';

export type UseFavoritesProps = {
  variantId?: string;
};

export const defaults = {
  data: {
    __typename: 'RestUserFavoritesData',
    attributes: {
      __typename: 'RestUserFavoritesAttributes',
      variantIds: []
    }
  }
};

const DEBOUNCE_AMOUNT_MS = 400;

const updateFavoritesCache = (cache: DataProxy, variantIdNumber: number, remove: boolean) => {
  const data = cache.readQuery<GetFavoritesResponse>({ query: GET_FAVORITES_QUERY });

  /* istanbul ignore if */
  if (!data) {
    return;
  }

  const variantIds = data.userFavorites.data.attributes.variantIds;
  const newVariantIds = remove
    ? variantIds.filter((id: number) => id !== variantIdNumber)
    : [...variantIds, variantIdNumber];

  const resultData: GetFavoritesResponse = {
    userFavorites: {
      __typename: 'RestUserFavorites',
      data: {
        __typename: 'RestUserFavoritesData',
        attributes: {
          __typename: 'RestUserFavoritesAttributes',
          variantIds: newVariantIds
        }
      }
    }
  };

  cache.writeQuery({
    query: GET_FAVORITES_QUERY,
    data: resultData
  });
};

export const useFavorites = ({ variantId }: UseFavoritesProps = {}) => {
  const variantIdNumber = variantId ? +variantId : 0; // TODO: SearchApi and UserApi disagree on types here

  const client = useApolloClient();
  const { user, favorites, getFavorites } = useContext(FavoritesContext);

  useEffect(() => {
    getFavorites();
  }, [getFavorites]);

  const [addFavorite] = useMutation(ADD_FAVORITE_MUTATION, {
    update(cache) {
      updateFavoritesCache(cache, variantIdNumber, false);
    }
  });

  const [removeFavorite] = useMutation(REMOVE_FAVORITE_MUTATION, {
    update(cache) {
      updateFavoritesCache(cache, variantIdNumber, true);
    }
  });

  const debouncedAddFavorite = useMemo(() => debounce(addFavorite, DEBOUNCE_AMOUNT_MS, true), [
    addFavorite
  ]);

  const debouncedRemoveFavorite = useMemo(
    () => debounce(removeFavorite, DEBOUNCE_AMOUNT_MS, true),
    [removeFavorite]
  );

  const isFavorited = useMemo(
    () => (variantIdNumber ? favorites.includes(variantIdNumber) : false),
    [favorites, variantIdNumber]
  );

  const handleFavorite = useCallback(async () => {
    if (!variantId) {
      // eslint-disable-next-line no-console
      console.warn('Unable to add/remove favorite: `variantId` is missing');
      return Promise.resolve();
    }

    // make sure favorites are fetched before running the mutation
    await client.query({ query: GET_FAVORITES_QUERY });

    if (isFavorited) {
      return debouncedRemoveFavorite({ variables: { id: variantId } });
    } else {
      return debouncedAddFavorite({ variables: { id: variantId } });
    }
  }, [variantId, client, isFavorited, debouncedRemoveFavorite, debouncedAddFavorite]);

  return { user, favorites, handleFavorite, isFavorited };
};
